import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import DynamicTable from "../../common/DynamicTable";
import moment from "moment";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";

const LeadDetails = () => {
  const location = useLocation();
  const campaignId = location?.state?.Campaign_id;
  const fromDate = location?.state?.fromDate;
  const toDate = location?.state?.toDate;
  const selectedProduct = location?.state?.selectedProduct;
  const backToTopRef = useRef(null);

  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoader, setFilterLoader] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentLeadId, setCurrentLeadId] = useState(null);
  const [detailsText, setDetailsText] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  console.log(campaignId);

  const handleViewDetails = (leadId, reason) => {
    setCurrentLeadId(leadId);
    setDetailsText(reason || "");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentLeadId(null);
    setDetailsText("");
  };

  const handleSubmitDetails = async () => {
    if (!detailsText.trim()) {
      toast.error("Please enter details");
      return;
    }
    setSubmitLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/addLeadDisposition`, {
        method: "POST",
        headers: {
          "content-Type": "application/json",
          session: `${localStorage.getItem("access_token")}`,
          Authkey: `${process.env.REACT_APP_API_AUTH_KEY}`,
        },
        body: JSON.stringify({
          lead_id: parseInt(currentLeadId, 10),
          reason: detailsText
        })
      });
      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        throw new Error(`API Endpoint issue. Server returned non-JSON data.`);
      }

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status} - ${result.message || 'Unknown'}`);
      }

      if (result.status === 1 || result.status === true || response.status === 200) {
        toast.success(result.message || "Details submitted successfully");
        handleCloseModal();
        if (campaignId) {
          fetchData({
            from_date: fromDate ? moment(fromDate).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
            to_date: toDate ? moment(toDate).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
            product: selectedProduct || "All",
            campaign_id: campaignId.toString()
          });
        }
      } else {
        toast.error(result.message || "Failed to submit details");
      }
    } catch (err) {
      toast.error(err.message || "An error occurred");
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch with passed dates/product from state, or fallback to today
    if (campaignId) {
      const payload = {
        from_date: fromDate ? moment(fromDate).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
        to_date: toDate ? moment(toDate).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
        product: selectedProduct || "All",
        campaign_id: campaignId.toString()
      };
      fetchData(payload);
    } else {
      setError("Campaign ID is missing.");
    }
  }, [campaignId]);

  const fetchData = async (payload) => {
    setLoading(true);
    setFilterLoader(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/getBuyerAnalyticsSoldLeads`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "content-Type": "application/json",
          session: `${localStorage.getItem("access_token")}`,
          Authkey: `${process.env.REACT_APP_API_AUTH_KEY}`,
        },
      });
      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        throw new Error(`API Endpoint issue. Server returned non-JSON data.`);
      }

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status} - ${result.message || 'Unknown'}`);
      }

      let fetchedData = [];

      if (result.status === 1 && result.responsedata && result.responsedata.results) {
        fetchedData = result.responsedata.results;
      } else if (result.data && Array.isArray(result.data)) {
        fetchedData = result.data;
      } else if (Array.isArray(result)) {
        fetchedData = result;
      } else if (result.status === 0) {
        setError(result.message || "No data found");
      }

      setData(fetchedData);

      if (fetchedData.length > 0) {
        // Generate columns dynamically based on keys of the first item
        const keysToHide = ["lead_id", "buyer_id", "sold_price", "sold_date", "reason"];
        const generatedColumns = Object.keys(fetchedData[0])
          .filter(key => !keysToHide.includes(key) && key !== "id")
          .map((key, index) => ({
            dataField: key,
            text: key.replace(/_/g, " ").toUpperCase(),
            sort: true,
            headerStyle: () => {
              return { textAlign: "center" };
            },
            style: { textAlign: "center" },
          }));

        // Add a fallback ID column if none exists for keyField
        if (!generatedColumns.find(c => c.dataField === 'id')) {
          fetchedData.forEach((item, index) => {
            if (!item.id) item.id = index + 1;
          });
        }

        const actionColumn = {
          dataField: "action",
          text: "ACTION",
          isDummyField: true,
          csvExport: false,
          formatter: (cell, row) => (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleViewDetails(row.lead_id, row.reason)}
            >
              Disposition
            </button>
          ),
          headerStyle: () => ({ textAlign: "center" }),
          style: { textAlign: "center" },
        };

        generatedColumns.push(actionColumn);

        setColumns(generatedColumns);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setFilterLoader(false);
    }
  };

  const handleFilterGenerateBtn = (filterData) => {
    let payload = {};
    if (filterData) {
      payload.from_date = moment(filterData?.fromDate).format("YYYY-MM-DD");
      payload.to_date = moment(filterData?.toDate).format("YYYY-MM-DD");
      payload.product = filterData.selectedProduct ? filterData.selectedProduct : "All";
      payload.campaign_id = filterData.campaign_id ? filterData.campaign_id.toString() : "All";
    }
    fetchData(payload);
  };
  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const payload = {
        from_date: fromDate ? moment(fromDate).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
        to_date: toDate ? moment(toDate).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
        campaign_id: campaignId ? campaignId.toString() : "",
        id: campaignId ? campaignId.toString() : ""
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/exportBuyerAnalyticsSoldLeadsCsv`, {
        method: "POST",
        headers: {
          "content-Type": "application/json",
          session: `${localStorage.getItem("access_token")}`,
          Authkey: `${process.env.REACT_APP_API_AUTH_KEY}`,
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error("Failed to export CSV");
      }

      const disposition = response.headers.get("content-disposition");
      let filename = `lead-details-${campaignId || 'export'}.csv`;
      
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      toast.error(err.message || "Error exporting CSV");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="m-4" ref={backToTopRef}>
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      <DynamicTable
        customHeader={(
          <div className="clientReportTitle">
            <div className="d-flex justify-content-between align-items-center mb-0 w-100">
              <div className="d-flex align-items-center gap-3">
                <Link
                  to="/report"
                  state={{
                    fromDate: location?.state?.fromDate,
                    toDate: location?.state?.toDate,
                    selectedProduct: location?.state?.selectedProduct,
                    report_type: location?.state?.report_type,
                    campaign_id: "All",
                    selectedType: location?.state?.selectedType
                  }}
                  className="text-blute"
                  style={{ fontSize: "1.2rem", cursor: "pointer" }}
                >
                  <i className="fa fa-arrow-left"></i>
                </Link>
                <h3 className="m-0">Lead Details {campaignId ? `[Campaign: ${campaignId}]` : ""}</h3>
              </div>
              {data && data.length > 0 && (
                <button
                  className="btn btn-success btn-sm px-3"
                  onClick={handleExportCSV}
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Exporting...</>
                  ) : (
                    <><i className="fa fa-download me-1"></i> Export CSV</>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
        mainLoader={loading && !filterLoader}
        backToTopRef={backToTopRef}
        formData={{ results: data }}
        filterLoader={filterLoader}
        handleFilterGenerateBtn={handleFilterGenerateBtn}
        filterData={["date", "campaign_id", "product"]}
        mode={"data"}
        customColumnsData={columns}
      />

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Disposition</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label>Disposition</label>
            <textarea
              className="form-control mt-2"
              rows="4"
              value={detailsText}
              onChange={(e) => setDetailsText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitDetails();
                }
              }}
              placeholder="Enter disposition here..."
            ></textarea>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSubmitDetails} disabled={submitLoading}>
            {submitLoading ? "Submitting..." : "Submit Disposition"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LeadDetails;
